import { BaseApiClient } from '../base-client'
import {
  API_ROUTES,
  ApiResponse,
  ApiKeyCredentialIntegrationType,
  interpolateRoute,
  type Listing,
} from '@super/shared-types'
import { withErrorHandling } from '../../utils'

export interface SyncListingsResponse {
  listings: Listing[]
  count: number
}

export interface SyncListingsStatsResponse {
  success: boolean
  recordsProcessed: number
  recordsCreated: number
  recordsUpdated: number
  recordsFailed: number
}

/**
 * ShowMojo Integration API Client
 * Handles all ShowMojo-specific API operations
 */
export class ShowMojoClient extends BaseApiClient {
  /**
   * Sync rental listings from ShowMojo
   * Triggers sync and then fetches the synced listings
   */
  async syncListings(): Promise<Listing[]> {
    const result = await withErrorHandling(
      async () => {
        // Step 1: Trigger the sync (synchronous operation)
        const url = interpolateRoute(API_ROUTES.INTEGRATIONS.SYNC_LISTINGS, {
          integrationType: ApiKeyCredentialIntegrationType.SHOWMOJO,
        })
        await this.request<ApiResponse<SyncListingsStatsResponse>>(url, {
          method: 'POST',
        })

        // Step 2: Fetch the synced listings
        const listingsResponse = await this.request<ApiResponse<Listing[]>>(
          API_ROUTES.LISTINGS.GET_ALL,
        )
        return listingsResponse.data ?? []
      },
      {
        errorContext: 'Error syncing ShowMojo listings',
      },
    )
    return result ?? []
  }

  /**
   * [ADMIN] Sync rental listings from ShowMojo for a specific company
   * Note: This triggers the sync and then fetches the listings separately
   */
  async syncAdminListings(companyId: string): Promise<Listing[]> {
    const result = await withErrorHandling(
      async () => {
        // Step 1: Trigger the sync (synchronous operation)
        const url = interpolateRoute(
          API_ROUTES.ADMIN.INTEGRATIONS.SYNC_LISTINGS,
          { integrationType: ApiKeyCredentialIntegrationType.SHOWMOJO },
        )
        await this.request<ApiResponse<SyncListingsStatsResponse>>(
          `${url}?companyId=${companyId}`,
          { method: 'POST' },
        )

        // Step 2: Fetch the synced listings
        const listingsResponse = await this.request<ApiResponse<Listing[]>>(
          `${API_ROUTES.ADMIN.LISTINGS.GET_ALL}?companyId=${companyId}`,
        )
        return listingsResponse.data ?? []
      },
      {
        errorContext: 'Error syncing admin ShowMojo listings',
      },
    )
    return result ?? []
  }

  /**
   * Create API key credentials for ShowMojo
   */
  async createCredentials(
    clientId: string,
    clientSecret: string,
  ): Promise<void> {
    await withErrorHandling(
      async () => {
        const url = interpolateRoute(
          API_ROUTES.INTEGRATIONS.API_KEY_CREDENTIALS.CREATE,
          { integrationType: ApiKeyCredentialIntegrationType.SHOWMOJO },
        )
        return await this.request<ApiResponse<{ message: string }>>(url, {
          method: 'POST',
          body: JSON.stringify({ clientId, clientSecret }),
        })
      },
      {
        errorContext: 'Error creating ShowMojo API key credentials',
      },
    )
  }

  /**
   * [ADMIN] Create API key credentials for ShowMojo for a specific company
   */
  async createAdminCredentials(
    companyId: string,
    clientId: string,
    clientSecret: string,
  ): Promise<void> {
    await withErrorHandling(
      async () => {
        const url = interpolateRoute(
          API_ROUTES.ADMIN.INTEGRATIONS.API_KEY_CREDENTIALS.CREATE,
          { integrationType: ApiKeyCredentialIntegrationType.SHOWMOJO },
        )
        return await this.request<ApiResponse<{ message: string }>>(
          `${url}?companyId=${companyId}`,
          {
            method: 'POST',
            body: JSON.stringify({ clientId, clientSecret }),
          },
        )
      },
      {
        errorContext: 'Error creating admin ShowMojo API key credentials',
      },
    )
  }

  /**
   * Update API key credentials for ShowMojo
   */
  async updateCredentials(
    clientId: string,
    clientSecret: string,
  ): Promise<void> {
    await withErrorHandling(
      async () => {
        const url = interpolateRoute(
          API_ROUTES.INTEGRATIONS.API_KEY_CREDENTIALS.UPDATE,
          { integrationType: ApiKeyCredentialIntegrationType.SHOWMOJO },
        )
        return await this.request<ApiResponse<{ message: string }>>(url, {
          method: 'PUT',
          body: JSON.stringify({ clientId, clientSecret }),
        })
      },
      {
        errorContext: 'Error updating ShowMojo API key credentials',
      },
    )
  }

  /**
   * [ADMIN] Update API key credentials for ShowMojo for a specific company
   */
  async updateAdminCredentials(
    companyId: string,
    clientId: string,
    clientSecret: string,
  ): Promise<void> {
    await withErrorHandling(
      async () => {
        const url = interpolateRoute(
          API_ROUTES.ADMIN.INTEGRATIONS.API_KEY_CREDENTIALS.UPDATE,
          { integrationType: ApiKeyCredentialIntegrationType.SHOWMOJO },
        )
        return await this.request<ApiResponse<{ message: string }>>(
          `${url}?companyId=${companyId}`,
          {
            method: 'PUT',
            body: JSON.stringify({ clientId, clientSecret }),
          },
        )
      },
      {
        errorContext: 'Error updating admin ShowMojo API key credentials',
      },
    )
  }

  /**
   * Disconnect ShowMojo integration
   * Uses the generic API key credentials disconnect endpoint
   */
  async disconnect(): Promise<void> {
    const url = interpolateRoute(
      API_ROUTES.INTEGRATIONS.API_KEY_CREDENTIALS.DELETE,
      { integrationType: ApiKeyCredentialIntegrationType.SHOWMOJO },
    )
    await withErrorHandling(
      async () => {
        return await this.request<ApiResponse<{ message: string }>>(url, {
          method: 'DELETE',
        })
      },
      {
        errorContext: 'Error disconnecting ShowMojo integration',
      },
    )
  }

  /**
   * [ADMIN] Disconnect ShowMojo integration for a specific company
   */
  async disconnectAdmin(companyId: string): Promise<void> {
    const url = interpolateRoute(
      API_ROUTES.ADMIN.INTEGRATIONS.API_KEY_CREDENTIALS.DELETE,
      { integrationType: ApiKeyCredentialIntegrationType.SHOWMOJO },
    )
    await withErrorHandling(
      async () => {
        return await this.request<ApiResponse<{ message: string }>>(
          `${url}?companyId=${companyId}`,
          {
            method: 'DELETE',
          },
        )
      },
      {
        errorContext: 'Error disconnecting admin ShowMojo integration',
      },
    )
  }
}

// Export singleton instance
export const showMojoClient = new ShowMojoClient()
